import Dinero from "dinero.js";

export function dollarsToCents(dollars: number | string): number {
  const numericValue =
    typeof dollars === "string" ? parseFloat(dollars) : dollars;

  if (isNaN(numericValue) || !isFinite(numericValue)) {
    throw new Error(
      `Invalid dollar amount: "${dollars}". Must be a valid finite number.`,
    );
  }

  if (numericValue < 0) {
    throw new Error(`Invalid dollar amount: "${dollars}". Cannot be negative.`);
  }

  // Round to nearest cent to handle floating point precision issues
  const cents = Math.round(numericValue * 100);

  // Ensure the result is an integer
  if (!Number.isInteger(cents)) {
    throw new Error(
      `Invalid dollar amount: "${dollars}". Cannot be converted to integer cents.`,
    );
  }

  const money = Dinero({ amount: cents, currency: "USD" });
  return money.getAmount();
}

export function centsToDollars(cents: number): number {
  // Ensure cents is an integer - round to nearest cent to handle any floating point precision issues
  const integerCents = Math.round(cents);

  if (!Number.isInteger(integerCents)) {
    throw new Error(
      `Invalid cents value: ${cents}. Must be a finite number that can be converted to an integer.`,
    );
  }

  const money = Dinero({ amount: integerCents, currency: "USD" });
  return money.toUnit();
}

export { formatCentsAsDollars } from "@shared/pricing-utils";

/**
 * Core validation logic for numeric input strings
 */
function validateNumericString(
  value: string,
  maxDecimalPlaces: number,
): { isValid: boolean; numericValue?: number } {
  // Handle empty and special cases
  if (value === "") return { isValid: true };
  if (value === ".") return { isValid: false };
  if (value.endsWith(".") && value.length > 1) return { isValid: true };

  try {
    // Check for leading/trailing whitespace
    if (value !== value.trim()) return { isValid: false };

    // Check for invalid characters using regex - only allow digits, one decimal point
    const validPattern = /^[0-9]+(\.[0-9]*)?$/;
    if (!validPattern.test(value)) return { isValid: false };

    // Check decimal places before parsing
    const decimalPlaces = (value.split(".")[1] || "").length;
    if (decimalPlaces > maxDecimalPlaces) return { isValid: false };

    // Now safely parse - we know it's a valid numeric string
    const numericValue = parseFloat(value);
    if (isNaN(numericValue) || !isFinite(numericValue) || numericValue < 0)
      return { isValid: false };

    return { isValid: true, numericValue };
  } catch (error) {
    return { isValid: false };
  }
}

export function isValidDollarAmount(value: string): boolean {
  const validation = validateNumericString(value, 2);
  if (!validation.isValid) return false;

  // If we have a numeric value, test with Dinero to ensure it's valid for currency operations
  if (validation.numericValue !== undefined) {
    try {
      Dinero({
        amount: Math.round(validation.numericValue * 100),
        currency: "USD",
      });
    } catch (error) {
      return false;
    }
  }

  return true;
}

export function isValidPercentageAmount(value: string): boolean {
  return validateNumericString(value, 4).isValid;
}

export function createMoneyFromCents(cents: number) {
  // Ensure cents is an integer - round to nearest cent to handle any floating point precision issues
  const integerCents = Math.round(cents);

  if (!Number.isInteger(integerCents)) {
    throw new Error(
      `Invalid cents value: ${cents}. Must be a finite number that can be converted to an integer.`,
    );
  }

  return Dinero({ amount: integerCents, currency: "USD" });
}

export function createMoneyFromDollars(dollars: number | string) {
  const cents = dollarsToCents(dollars);
  return Dinero({ amount: cents, currency: "USD" });
}

export function addMoney(cents1: number, cents2: number): number {
  const money1 = createMoneyFromCents(cents1);
  const money2 = createMoneyFromCents(cents2);
  const sum = money1.add(money2);
  return sum.getAmount();
}

export function multiplyMoney(cents: number, multiplier: number): number {
  const money = createMoneyFromCents(cents);
  const product = money.multiply(multiplier);
  return product.getAmount();
}

export function roundToTwoDecimalPlaces(value: number): number {
  return Math.round(value * 100) / 100;
}

export function percentageToDollarAmount(
  percentage: number,
  totalAmountCents: number,
): number {
  const discountCents = multiplyMoney(totalAmountCents, percentage / 100);
  const dollarAmount = centsToDollars(discountCents);
  return roundToTwoDecimalPlaces(dollarAmount);
}

export function roundToFourDecimalPlaces(value: number): number {
  return Math.round(value * 10000) / 10000;
}

export function dollarAmountToPercentage(
  dollarAmount: number,
  totalAmountCents: number,
): number {
  if (totalAmountCents === 0) return 0;

  const dollarCents = dollarsToCents(dollarAmount);
  const totalMoney = createMoneyFromCents(totalAmountCents);
  const percentage = (dollarCents / totalMoney.getAmount()) * 100;

  return roundToFourDecimalPlaces(percentage);
}

export function isValidNumericAmount(
  value: string,
  maxDecimalPlaces: number = 2,
): boolean {
  return validateNumericString(value, maxDecimalPlaces).isValid;
}

// Enhanced validation functions with configurable limits
export function isValidDollarAmountWithLimits(
  value: string,
  maxValue: number = 999999.99,
): boolean {
  const validation = validateNumericString(value, 2);
  if (!validation.isValid) return false;

  // If we have a numeric value, check the maximum limit
  if (validation.numericValue !== undefined) {
    if (validation.numericValue > maxValue) return false;

    // Test with Dinero to ensure it's valid for currency operations
    try {
      Dinero({
        amount: Math.round(validation.numericValue * 100),
        currency: "USD",
      });
    } catch (error) {
      return false;
    }
  }

  return true;
}

export function isValidPercentageAmountWithLimits(
  value: string,
  maxValue: number = 1000,
  maxDecimalPlaces: number = 4,
): boolean {
  const validation = validateNumericString(value, maxDecimalPlaces);
  if (!validation.isValid) return false;

  // If we have a numeric value, check the maximum limit
  if (validation.numericValue !== undefined) {
    if (validation.numericValue > maxValue) return false;
  }

  return true;
}

export function isValidNumericAmountWithLimits(
  value: string,
  maxValue: number = 9999.99,
  maxDecimalPlaces: number = 2,
): boolean {
  const validation = validateNumericString(value, maxDecimalPlaces);
  if (!validation.isValid) return false;

  // If we have a numeric value, check the maximum limit
  if (validation.numericValue !== undefined) {
    if (validation.numericValue > maxValue) return false;
  }

  return true;
}

/**
 * Format unit of measure for display
 *
 * For products: Uses the unitOfMeasure field from the products table
 * For custom parts: Returns "each" (custom parts don't have unitOfMeasure)
 *
 * @param unitOfMeasure - The unit of measure from the product (e.g., "EA", "BOX", "FT")
 * @param isCustomPart - Whether this is a custom part (defaults to false)
 * @returns Lowercase unit of measure (e.g., "ea", "box", "ft") or "each" for custom parts
 *
 * Future: Could add conversion from codes to full names (e.g., "EA" -> "each", "BOX" -> "box")
 */
export function formatUnitOfMeasure(
  unitOfMeasure: string | undefined | null,
  isCustomPart: boolean = false,
): string {
  // Custom parts always use "each"
  if (isCustomPart || unitOfMeasure === "ea") {
    return "each";
  }

  // Products should have unitOfMeasure - log error if missing but fall back gracefully
  if (!unitOfMeasure) {
    console.error(
      "Product is missing required unitOfMeasure field - falling back to 'ea'",
    );
    return "ea";
  }

  // Lowercase the unit of measure for consistent display
  return unitOfMeasure.toLowerCase();
}
